import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Card, Badge, Spinner, Alert, Tabs, Tab, Form, Button, Offcanvas } from 'react-bootstrap';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getActualDocument, getActualHtml, getActualDocumentLinks, getDocumentTopics } from '@/services/api';
import { TopicBadge } from '@/components/TopicBadge';
import { ExportPanel } from '@/features/export/components/ExportPanel';
import { RedactionCompareModal } from '@/features/redaction-compare/components/RedactionCompareModal';

// ======== TYPES ========

export interface ParsedElement {
  type:
    | 'chapter'
    | 'article_header'
    | 'doc_title'
    | 'law_name'
    | 'part_text'
    | 'point'
    | 'subpoint'
    | 'paragraph'
    | 'redaction_note'
    | 'application_note'
    | 'added'
    | 'removed'
    | 'modified';
  content: string;
  num?: string;
  id?: string;
}

interface TreeItem {
  id: string;
  title: string;
  number?: string;
  type: 'chapter' | 'article';
  elementIndex: number;
  children?: TreeItem[];
}

// ======== STYLES ========

const DocumentStyles = () => (
  <style>{`
    .doc-tree { font-size: 13.5px; line-height: 1.45; }
    .doc-tree-item { border-radius: 6px; transition: all 0.12s ease; cursor: pointer; margin: 1px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .doc-tree-item:hover { background: #eff6ff; }
    .doc-tree-item.active { background: #dbeafe; border-left: 3px solid #2563eb; font-weight: 600; }
    .doc-tree-chapter { font-weight: 700; font-size: 14px; color: #0f172a; padding: 8px 12px; margin-top: 4px; background: #f1f5f9; border-radius: 6px; }
    .doc-tree-article { font-weight: 400; color: #334155; padding: 4px 12px 4px 20px; border-left: 2px solid transparent; font-size: 13px; }
    .doc-tree-article:hover { border-left-color: #2563eb; }
    .doc-tree-number { color: #2563eb; font-weight: 600; margin-right: 6px; font-variant-numeric: tabular-nums; }

    .law-viewer { font-family: "Times New Roman", "Liberation Serif", Times, serif; font-size: 18px; line-height: 1.6; color: #111827; }
    .law-doc-header { text-align: center; margin-bottom: 36px; padding-bottom: 24px; border-bottom: 2px solid #1f2937; }
    .law-doc-header h1 { font-size: 20px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 16px; line-height: 1.4; }
    .law-meta-line { font-size: 15px; color: #374151; margin: 5px 0; }

    .chapter-block { margin: 48px 0 32px 0; padding: 20px 0; border-top: 2px solid #1f2937; border-bottom: 1px solid #e5e7eb; scroll-margin-top: 20px; }
    .chapter-title { font-size: 18px; font-weight: 700; text-align: center; text-transform: uppercase; letter-spacing: 0.6px; color: #0f172a; }

    .article-block { margin: 32px 0; padding: 0 0 24px 0; border-bottom: 1px solid #e5e7eb; scroll-margin-top: 20px; }
    .article-header { font-weight: 700; font-size: 17px; margin-bottom: 20px; margin-top: 10px; text-align: center; color: #1f2937; line-height: 1.4; }

    .doc-title { font-weight: 700; text-align: center; margin-top: 20px; font-size: 19px; }
    .law-name { font-weight: 700; text-align: center; margin: 16px 0; font-size: 18px; }

    .part-text { margin: 12px 0 8px 0; font-weight: bold; display: flex; align-items: baseline; }
    .part-num { margin-right: 8px; min-width: 20px; flex-shrink: 0; }
    .part-content { flex: 1; }

    .point { margin: 6px 0 6px 20px; display: flex; align-items: baseline; }
    .point-num { font-weight: normal; margin-right: 8px; min-width: 25px; flex-shrink: 0; }
    .point-content { flex: 1; }

    .subpoint { margin: 4px 0 4px 40px; display: flex; align-items: baseline; }
    .subpoint-num { font-weight: normal; margin-right: 6px; min-width: 20px; flex-shrink: 0; }
    .subpoint-content { flex: 1; }

    .abz { margin: 8px 0; text-indent: 1.5em; }

    .redaction-note { font-size: 13px; color: #6b7280; font-style: italic; margin: 4px 0 4px 20px; padding: 4px 8px; background: #f9fafb; border-left: 3px solid #d1d5db; display: block; }
    .redaction-inline { font-size: 13px; color: #6b7280; font-style: italic; margin-left: 4px; }

    .application-note { font-size: 14px; color: #374151; margin: 12px 0; padding: 8px 12px; background: #fefce8; border-left: 3px solid #eab308; }

    .ed-text { background-color: #ecfdf5; color: #047857; padding: 0 2px; border-radius: 2px; }
    .sup-index { font-size: 0.75em; vertical-align: super; line-height: 0; position: relative; top: -0.4em; }
    .doc-link { color: #2563eb; text-decoration: underline; cursor: pointer; }

    .custom-scroll::-webkit-scrollbar { width: 6px; }
    .custom-scroll::-webkit-scrollbar-track { background: transparent; }
    .custom-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

    @media (max-width: 991px) { .doc-sidebar { display: none; } .law-viewer { font-size: 16px; padding: 0 4px; } }
  `}</style>
);

// ======== UTILS ========

function extractHtml(response: any): string {
  if (!response) return '';
  if (typeof response === 'string') return response;
  if (response.redtext) return response.redtext;
  if (response.html) return response.html;
  return '';
}

function unescapeJsonString(str: string): string {
  if (!str) return '';
  return str.replace(/\\\\"/g, '"').replace(/\\\\r\\\\n/g, '\n').replace(/\\\\n/g, '\n');
}

function decodeMojibake(str: string): string {
  if (!str) return '';
  if (/[а-яёА-ЯЁ]/.test(str)) return str;
  try {
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      bytes[i] = str.charCodeAt(i);
    }
    return new TextDecoder('windows-1251').decode(bytes);
  } catch (e) {
    return str;
  }
}

function processHtmlString(html: string, gohashMap: Record<string, string> = {}): string {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const container = doc.body.firstChild as HTMLElement;
  if (!container) return html;

  container.querySelectorAll('span.ed').forEach(el => {
    (el as HTMLElement).className = 'ed-text';
  });
  container.querySelectorAll('span.mark, span.markx').forEach(el => {
    (el as HTMLElement).className = 'redaction-inline';
  });
  container.querySelectorAll('span.W9').forEach(el => {
    (el as HTMLElement).className = 'sup-index';
  });
  container.querySelectorAll('span.cmd').forEach(el => {
    const htmlEl = el as HTMLElement;
    const cmdprm = htmlEl.getAttribute('cmdprm');
    let gohash = '';
    if (cmdprm) {
      const match = cmdprm.match(/gohash=([a-f0-9]+)/i);
      if (match) gohash = match[1];
    }
    const targetDocid = gohashMap[gohash];
    if (targetDocid) {
      const link = document.createElement('a');
      link.href = `/actual/document/${targetDocid}`;
      link.className = 'doc-link';
      link.innerHTML = htmlEl.innerHTML;
      link.style.color = '#2563eb';
      link.style.textDecoration = 'underline';
      link.style.cursor = 'pointer';
      htmlEl.parentNode?.replaceChild(link, htmlEl);
    } else {
      htmlEl.className = 'doc-link';
      htmlEl.removeAttribute('onclick');
      htmlEl.style.color = '#2563eb';
      htmlEl.style.textDecoration = 'underline';
      htmlEl.style.cursor = 'pointer';
    }
  });
  container.querySelectorAll('span.W2').forEach(el => {
    (el as HTMLElement).style.fontStyle = 'italic';
    (el as HTMLElement).className = '';
  });
  container.querySelectorAll('span.W4').forEach(el => {
    (el as HTMLElement).style.fontWeight = 'bold';
    (el as HTMLElement).className = '';
  });

  return container.innerHTML;
}

export function parsePravoHtml(html: string, gohashMap: Record<string, string> = {}): { elements: ParsedElement[]; tree: TreeItem[] } {
  if (!html) return { elements: [], tree: [] };

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements: ParsedElement[] = [];
  const tree: TreeItem[] = [];

  let currentChapter: TreeItem | null = null;

  const paragraphs = Array.from(doc.querySelectorAll('p'));

  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i];
    const className = p.className?.trim() || '';
    const id = p.id;
    const text = p.textContent?.trim() || '';

    if (!text && !p.querySelector('img')) continue;

    if (className === 'H') {
      if (/^Глава\s+[\dIVX]+/i.test(text)) {
        currentChapter = { id: `ch-${i}`, title: text, type: 'chapter', elementIndex: elements.length, children: [] };
        tree.push(currentChapter);
        const chNumMatch = text.match(/^Глава\s+([\dIVX]+)/i);
        const chNum = chNumMatch ? chNumMatch[1] : '';
        const chCleanedHtml = p.innerHTML.replace(/^\s*Глава\s+[\dIVX]+\.\s*/i, '');
        elements.push({ type: 'chapter', content: processHtmlString(chCleanedHtml, gohashMap), id, num: chNum });
      } else if (/^Статья\s+\d+/i.test(text)) {
        const match = text.match(/Статья\s+(\d+)/i);
        const num = match ? match[1] : '';
        const article = {
          id: `art-${num}`,
          title: text,
          number: num,
          type: 'article' as const,
          elementIndex: elements.length
        };

        if (currentChapter) currentChapter.children?.push(article);
        else tree.push(article);

        const artCleanedHtml = p.innerHTML.replace(/^\s*Статья\s+\d+\.\s*/i, '');
        elements.push({ type: 'article_header', content: processHtmlString(artCleanedHtml, gohashMap), id, num });
      } else {
        const chNumMatch = text.match(/^Глава\s+([\dIVX]+)/i);
        const chNum = chNumMatch ? chNumMatch[1] : '';
        const chCleanedHtml = p.innerHTML.replace(/^\s*Глава\s+[\dIVX]+\.\s*/i, '');
        elements.push({ type: 'chapter', content: processHtmlString(chCleanedHtml, gohashMap), id, num: chNum });
      }
      continue;
    }

    if (className === 'T') { elements.push({ type: 'doc_title', content: processHtmlString(p.innerHTML, gohashMap), id }); continue; }
    if (className === 'Z') { elements.push({ type: 'law_name', content: processHtmlString(p.innerHTML, gohashMap), id }); continue; }

    if (className === 'A') {
      elements.push({ type: 'application_note', content: processHtmlString(p.innerHTML, gohashMap), id });
      continue;
    }

    if (/^\(.*(?:редакции|дополнение|утратил силу|особенности применения)/i.test(text)) {
       elements.push({ type: 'redaction_note', content: processHtmlString(p.innerHTML, gohashMap), id });
       continue;
    }

    const partMatch = text.match(/^(\d+)\.\s*/);
    if (partMatch) {
      const numStr = partMatch[1];

      if (numStr.length <= 3) {
        const restOfText = text.substring(partMatch[0].length);
        const isLawRef = /^(от\s+\d{1,2}\.|№\s*\d|Федерального|закона)/i.test(restOfText);
        const isDate = /^(\d{1,2}\s+)?(января|февраля|марта|апреля|мая|июня|июля|августа|сентября|октября|ноября|декабря)/i.test(restOfText);

        if (!isLawRef && !isDate) {
          const cleanedHtml = p.innerHTML.replace(/^(\s*<[^>]+>\s*)*\d+\.\s*/, '');
          elements.push({
            type: 'part_text',
            num: numStr + '.',
            content: processHtmlString(cleanedHtml, gohashMap),
            id
          });
          continue;
        }
      }
    }

    const pointMatch = text.match(/^(\d+)[\)\.]\s*/);
    if (pointMatch) {
       const numStr = pointMatch[1];
       const separator = text[numStr.length];

       if (numStr.length <= 2) {
         const cleanedHtml = p.innerHTML.replace(/^(\s*<[^>]+>\s*)*\d+[\)\.]\s*/, '');
         elements.push({
           type: 'point',
           num: numStr + separator,
           content: processHtmlString(cleanedHtml, gohashMap),
           id
         });
         continue;
       }
    }

    const subMatch = text.match(/^([а-яёa-z])[\)\.]\s*/i);
    if (subMatch) {
      const cleanedHtml = p.innerHTML.replace(/^(\s*<[^>]+>\s*)*[а-яёa-z][\)\.]\s*/i, '');
      elements.push({
        type: 'subpoint',
        num: subMatch[1] + ')',
        content: processHtmlString(cleanedHtml, gohashMap),
        id
      });
      continue;
    }

    elements.push({
      type: 'paragraph',
      content: processHtmlString(p.innerHTML, gohashMap),
      id
    });
  }

  return { elements, tree };
}

// ======== RENDERERS ========

const renderHtmlNodes = (html: string, key: string | number): React.ReactNode => {
  if (!html) return null;
  return <span key={key} dangerouslySetInnerHTML={{ __html: html }} />;
};

// ======== COMPONENT ========

export const ActualDocumentPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const docId = params.id || '';
  const [searchParams] = useSearchParams();

  const [doc, setDoc] = useState<any>(null);
  const [elements, setElements] = useState<ParsedElement[]>([]);
  const [treeStructure, setTreeStructure] = useState<TreeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [searchInDoc, setSearchInDoc] = useState('');
  const [activeTab, setActiveTab] = useState('structure');
  const [docLinks, setDocLinks] = useState<any[]>([]);
  const [gohashMap, setGohashMap] = useState<Record<string, string>>({});
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [docTopics, setDocTopics] = useState<any[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);
  const elementRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (!docId) return;
    loadDocument(docId);
  }, [docId]);

  useEffect(() => {
    if (!docId) return;
    getDocumentTopics(docId)
      .then((r: any) => setDocTopics(r.topics || []))
      .catch(() => setDocTopics([]));
  }, [docId]);

  useEffect(() => {
    elementRefs.current.clear();
  }, [docId]);

  const docLinksRef = useRef<any[]>([]);
  useEffect(() => {
    docLinksRef.current = docLinks;
  }, [docLinks]);

  useEffect(() => {
    const container = document.querySelector('.law-viewer');
    if (!container) return;

    const links = container.querySelectorAll('.doc-link');
    const handlers: Array<() => void> = [];

    links.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const cmdprm = htmlEl.getAttribute('cmdprm');
      let gohash = '';
      if (cmdprm) {
        const match = cmdprm.match(/gohash=([a-f0-9]+)/i);
        if (match) gohash = match[1];
      }

      const handler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();

        const linkText = htmlEl.textContent || '';
        console.log('[DocLink Click] Text:', linkText, 'gohash:', gohash, 'docLinks count:', docLinksRef.current.length);

        const targetDocid = gohashMap[gohash];
        if (targetDocid) {
          console.log('[DocLink Click] Found via gohashMap:', targetDocid);
          window.location.href = `/actual/document/${targetDocid}`;
          return;
        }

        const linkNum = linkText.match(/№\s*([\d\-]+)/)?.[1] || '';
        const linkDate = linkText.match(/(\d{1,2}\.\d{2}\.\d{4})/)?.[1] || '';

        console.log('[DocLink Click] Searching. linkNum:', linkNum, 'linkDate:', linkDate);
        console.log('[DocLink Click] Available links:', docLinksRef.current.map((l: any) => ({ num: l.target_number, name: l.target_docname?.slice(0, 30) })));

        const foundLink = docLinksRef.current.find((l: any) => {
          if (linkNum && l.target_number && String(l.target_number).includes(linkNum)) return true;
          if (linkDate && l.target_docpassing && l.target_docpassing.includes(linkDate)) return true;
          if (l.target_docname && linkText.length > 3) {
            const normalizedText = linkText.toLowerCase().replace(/[^а-яa-z0-9\s]/g, '').slice(0, 30);
            const normalizedName = l.target_docname.toLowerCase().replace(/[^а-яa-z0-9\s]/g, '').slice(0, 30);
            if (normalizedName.includes(normalizedText) || normalizedText.includes(normalizedName)) return true;
          }
          return false;
        });

        if (foundLink) {
          console.log('[DocLink Click] Found link:', foundLink.target_docid, foundLink.target_docname);
          window.location.href = `/actual/document/${foundLink.target_docid}`;
        } else {
          console.log('[DocLink Click] No link found, searching by text');
          window.location.href = `/actual?search=${encodeURIComponent(linkText)}`;
        }
      };

      el.addEventListener('click', handler);
      handlers.push(() => el.removeEventListener('click', handler));
    });

    return () => {
      handlers.forEach(cleanup => cleanup());
    };
  }, [elements, gohashMap]);

  const loadDocument = async (id: string) => {
    setLoading(true);
    setError(null);
    setDocLinks([]);
    setGohashMap({});
    try {
      const [docRes, linksRes] = await Promise.all([
        getActualDocument(id),
        getActualDocumentLinks(id).catch(() => ({ links: [] }))
      ]);
      setDoc(docRes?.document || null);
      console.log('[loadDocument] linksRes:', linksRes);
      console.log('[loadDocument] linksRes?.links:', linksRes?.links);
      setDocLinks(linksRes?.links || []);

      if (docRes?.document?.actual_redid != null) {
        const redId = String(docRes.document.actual_redid);
        const htmlRes = await getActualHtml(redId).catch(() => null);
        let rawHtml = extractHtml(htmlRes);

        if (rawHtml) {
          rawHtml = unescapeJsonString(rawHtml);
          rawHtml = decodeMojibake(rawHtml);

          const hashMap: Record<string, string> = {};
          if (linksRes?.links) {
            for (const link of linksRes.links) {
              if (link.target_docid) {
                hashMap[String(link.target_docid)] = String(link.target_docid);
              }
            }
          }
          setGohashMap(hashMap);

          const { elements: parsedElements, tree } = parsePravoHtml(rawHtml, hashMap);
          setElements(parsedElements);
          setTreeStructure(tree);
        } else {
          setError('Не удалось загрузить текст документа');
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Ошибка загрузки документа');
    } finally {
      setLoading(false);
    }
  };

  const scrollToIndex = useCallback((index: number) => {
    const el = elementRefs.current.get(index);
    if (el && contentRef.current) {
      contentRef.current.scrollTo({ top: el.offsetTop - 20, behavior: 'smooth' });
    }
  }, []);

  const handleTreeClick = useCallback((item: TreeItem) => {
    setActiveNodeId(item.id);
    setShowMobileNav(false);
    scrollToIndex(item.elementIndex);
  }, [scrollToIndex]);

  const searchResults = useMemo(() => {
    if (!searchInDoc.trim()) return { tree: treeStructure, highlightIndices: new Set<number>() };
    const lower = searchInDoc.toLowerCase();

    const highlightIndices = new Set<number>();
    elements.forEach((el, idx) => {
      const text = el.content.replace(/<[^>]+>/g, '').toLowerCase();
      if (text.includes(lower)) {
        highlightIndices.add(idx);
      }
    });

    const filterItems = (items: TreeItem[]): TreeItem[] => {
      return items.reduce((acc, item) => {
        const matchesTitle = item.title.toLowerCase().includes(lower);
        const hasHighlightedContent = elements
          .slice(item.elementIndex, item.children?.length
            ? item.children[item.children.length - 1].elementIndex + 5
            : item.elementIndex + 20)
          .some((el, offset) => highlightIndices.has(item.elementIndex + offset));
        const filteredChildren = item.children ? filterItems(item.children) : [];

        if (matchesTitle || hasHighlightedContent || filteredChildren.length > 0) {
          acc.push({ ...item, children: filteredChildren.length > 0 ? filteredChildren : item.children });
        }
        return acc;
      }, [] as TreeItem[]);
    };

    return { tree: filterItems(treeStructure), highlightIndices };
  }, [treeStructure, elements, searchInDoc]);

  const filteredTree = searchResults.tree;

  const renderElement = useCallback((el: ParsedElement, idx: number) => {
    const refCallback = (elRef: HTMLDivElement | null) => {
      if (elRef) elementRefs.current.set(idx, elRef);
    };

    const isHighlighted = searchInDoc.trim() && searchResults.highlightIndices.has(idx);
    const highlightStyle = isHighlighted ? { backgroundColor: '#fef08a', padding: '2px 4px', borderRadius: '4px' } : undefined;

    const isStructural = ['chapter', 'article_header'].includes(el.type);
    const containerClass = isStructural
      ? (el.type === 'chapter' ? 'chapter-block' : 'article-block')
      : undefined;

    switch (el.type) {
      case 'chapter':
        return (
          <div key={idx} ref={refCallback} className={containerClass} id={el.id} style={highlightStyle}>
            <div className="chapter-title">
              <span style={{color: '#2563eb', marginRight: 8}}>Глава {el.num}</span>
              {renderHtmlNodes(el.content, idx)}
            </div>
          </div>
        );
      case 'article_header':
        return (
          <div key={idx} ref={refCallback} className={containerClass} id={el.id} style={highlightStyle}>
            <div className="article-header">
              <span style={{color: '#2563eb', marginRight: 8}}>Статья {el.num}</span>
              {renderHtmlNodes(el.content, idx)}
            </div>
          </div>
        );
      case 'doc_title':
        return <div key={idx} className="doc-title" style={highlightStyle}>{renderHtmlNodes(el.content, idx)}</div>;
      case 'law_name':
        return <div key={idx} className="law-name" style={highlightStyle}>{renderHtmlNodes(el.content, idx)}</div>;
      case 'part_text':
        return (
          <div key={idx} ref={refCallback} className="part-text" id={el.id} style={highlightStyle}>
            <span className="part-num">{el.num}</span>
            <span className="part-content">{renderHtmlNodes(el.content, idx)}</span>
          </div>
        );
      case 'point':
        return (
          <div key={idx} ref={refCallback} className="point" id={el.id} style={highlightStyle}>
            <span className="point-num">{el.num}</span>
            <span className="point-content">{renderHtmlNodes(el.content, idx)}</span>
          </div>
        );
      case 'subpoint':
        return (
          <div key={idx} ref={refCallback} className="subpoint" id={el.id} style={highlightStyle}>
            <span className="subpoint-num">{el.num}</span>
            <span className="subpoint-content">{renderHtmlNodes(el.content, idx)}</span>
          </div>
        );
      case 'paragraph':
        return <p key={idx} ref={refCallback} className="abz" id={el.id} style={highlightStyle}>{renderHtmlNodes(el.content, idx)}</p>;
      case 'redaction_note':
        return <div key={idx} ref={refCallback} className="redaction-note" id={el.id} style={highlightStyle}>{renderHtmlNodes(el.content, idx)}</div>;
      case 'application_note':
        return <div key={idx} ref={refCallback} className="application-note" id={el.id} style={highlightStyle}>{renderHtmlNodes(el.content, idx)}</div>;
      default:
        return null;
    }
  }, [searchInDoc, searchResults.highlightIndices]);

  const renderTree = useCallback((items: TreeItem[], depth = 0) => {
    return items.map((item) => {
      const isActive = activeNodeId === item.id;
      const isChapter = item.type === 'chapter';

      return (
        <div key={item.id}>
          <div
            className={`doc-tree-item ${isActive ? 'active' : ''} ${isChapter ? 'doc-tree-chapter' : 'doc-tree-article'}`}
            style={{ paddingLeft: isChapter ? 12 : 12 + depth * 14 }}
            onClick={() => handleTreeClick(item)}
            title={item.title}
          >
            {isChapter && item.title}
            {!isChapter && item.title}
          </div>
          {item.children && item.children.length > 0 && renderTree(item.children, depth + 1)}
        </div>
      );
    });
  }, [activeNodeId, handleTreeClick]);

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" variant="primary" />
      <p className="text-muted mt-3">Загрузка и парсинг документа...</p>
    </Container>
  );

  if (error) return (
    <Container className="mt-5">
      <Alert variant="danger"><i className="bi bi-exclamation-triangle me-2"></i>{error}</Alert>
    </Container>
  );

  if (!doc) return (
    <Container className="mt-5">
      <Alert variant="warning">Документ не найден</Alert>
    </Container>
  );

  return (
    <>
      <DocumentStyles />
      <Container fluid className="mt-3 px-3">
        <Card className="mb-3 shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)', color: 'white' }}>
          <Card.Body className="py-3">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Link to="/actual" className="btn btn-outline-light btn-sm"><i className="bi bi-arrow-left me-1"></i>Назад</Link>
                  <Badge bg="light" text="dark" className="fs-6"><i className="bi bi-bank me-1"></i>actual.pravo.gov.ru</Badge>
                </div>
                <h4 className="mb-2" style={{ fontWeight: 600, lineHeight: 1.4 }}>{doc.docname}</h4>
                <div className="d-flex gap-2 flex-wrap">
                  {doc.docpassing && <Badge bg="warning" text="dark">{doc.docpassing}</Badge>}
                  {doc.adoption_number && <Badge bg="info">№ {doc.adoption_number}</Badge>}
                </div>
                {docTopics.length > 0 && (
                  <div className="d-flex flex-wrap align-items-center mt-2 pt-2" style={{borderTop:'1px solid rgba(255,255,255,0.2)'}}>
                    <span className="me-2 text-white-50 small"><i className="bi bi-tags me-1"></i>Темы:</span>
                    {docTopics.map((t: any) => (
                      <TopicBadge key={t.code} topic={t} showConfidence />
                    ))}
                  </div>
                )}
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setShowCompareModal(true)}
                >
                  <i className="bi bi-arrow-left-right me-1"></i>Сравнить редакции
                </Button>
                <ExportPanel
                  elements={elements}
                  docName={doc.docname}
                  docPassing={doc.docpassing}
                  docNumber={doc.adoption_number}
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        <div className="row g-3">
          <div className="col-lg-3 d-none d-lg-block doc-sidebar">
            <Card className="shadow-sm border-0 sticky-top" style={{ top: '10px', maxHeight: 'calc(100vh - 120px)' }}>
              <Card.Header className="bg-white border-bottom py-2">
                <div className="d-flex align-items-center justify-content-between">
                  <span className="fw-bold text-dark"><i className="bi bi-list-nested me-2"></i>Структура</span>
                  <Badge bg="secondary">{treeStructure.length}</Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-0 custom-scroll" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                <Form.Control
                  type="text"
                  placeholder="Поиск по документу..."
                  className="m-2"
                  style={{ width: 'calc(100% - 16px)', fontSize: 13 }}
                  value={searchInDoc}
                  onChange={(e) => setSearchInDoc(e.target.value)}
                />
                <div className="doc-tree px-2 pb-2">
                  {filteredTree.length > 0 ? renderTree(filteredTree) : <div className="text-center text-muted p-2">Ничего не найдено</div>}
                </div>
              </Card.Body>
            </Card>
          </div>

          <div className="d-lg-none mb-2">
            <Button variant="outline-primary" size="sm" onClick={() => setShowMobileNav(true)}><i className="bi bi-list me-2"></i>Структура</Button>
          </div>

          <div className="col-lg-9">
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'structure')} className="mb-3">
              <Tab eventKey="structure" title={<span><i className="bi bi-layout-text-sidebar me-1"></i>Текст закона</span>}>
                <Card className="shadow-sm border-0">
                  <Card.Body className="p-4">
                    <div 
                      className="law-viewer custom-scroll" 
                      ref={contentRef} 
                      style={{ maxHeight: '75vh', overflowY: 'auto' }}
                      onClick={async (e) => {
                        const target = e.target as HTMLElement;
                        const docLink = target.closest('.doc-link') as HTMLElement | null;
                        if (!docLink) return;

                        e.preventDefault();
                        e.stopPropagation();

                        const cmdprm = docLink.getAttribute('cmdprm');
                        let gohash = '';
                        if (cmdprm) {
                          const match = cmdprm.match(/gohash=([a-f0-9]+)/i);
                          if (match) gohash = match[1];
                        }

                        const linkText = docLink.textContent || '';
                        console.log('[DocLink Click] Text:', linkText, 'gohash:', gohash);

                        const targetDocid = gohashMap[gohash];
                        if (targetDocid) {
                          console.log('[DocLink Click] Found via gohashMap:', targetDocid);
                          window.location.href = `/actual/document/${targetDocid}`;
                          return;
                        }

                        if (gohash) {
                          try {
                            const res = await fetch(`/api/actual/gohash/${gohash}`);
                            if (res.ok) {
                              const data = await res.json();
                              if (data.docid) {
                                console.log('[DocLink Click] Found via API:', data.docid);
                                window.location.href = `/actual/document/${data.docid}`;
                                return;
                              }
                            }
                          } catch (err) {
                            console.log('[DocLink Click] API error:', err);
                          }
                        }

                        const linkNum = linkText.match(/№\s*([\d\-]+)/)?.[1] || '';
                        const linkDate = linkText.match(/(\d{1,2}\.\d{2}\.\d{4})/)?.[1] || '';

                        const foundLink = docLinks.find((l: any) => {
                          if (linkNum && l.target_number && String(l.target_number).includes(linkNum)) return true;
                          if (linkDate && l.target_docpassing && l.target_docpassing.includes(linkDate)) return true;
                          if (l.target_docname && linkText.length > 3) {
                            const normalizedText = linkText.toLowerCase().replace(/[^а-яa-z0-9\s]/g, '').slice(0, 30);
                            const normalizedName = l.target_docname.toLowerCase().replace(/[^а-яa-z0-9\s]/g, '').slice(0, 30);
                            if (normalizedName.includes(normalizedText) || normalizedText.includes(normalizedName)) return true;
                          }
                          return false;
                        });

                        if (foundLink) {
                          console.log('[DocLink Click] Found link:', foundLink.target_docid);
                          window.location.href = `/actual/document/${foundLink.target_docid}`;
                          return;
                        }

                        console.log('[DocLink Click] No link found, searching by text');
                        window.location.href = `/actual?search=${encodeURIComponent(linkText)}`;
                      }}
                    >
                      <div className="law-doc-header">
                        <h1>{doc.docname}</h1>
                        <div className="law-meta-line">{doc.docpassing}</div>
                        {doc.adoption_number && <div className="law-meta-line">№ {doc.adoption_number}</div>}
                        {doc.adoption_date && <div className="law-meta-line">от {new Date(doc.adoption_date).toLocaleDateString('ru-RU')}</div>}
                      </div>

                      {elements.length === 0 ? (
                        <Alert variant="info">Текст документа пуст</Alert>
                      ) : (
                        elements.map((el, idx) => renderElement(el, idx))
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </div>
        </div>

        <Offcanvas show={showMobileNav} onHide={() => setShowMobileNav(false)} placement="start">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title><i className="bi bi-list-nested me-2"></i>Структура</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="p-0">
            <Form.Control type="text" placeholder="Поиск..." className="m-3" style={{ width: 'calc(100% - 24px)' }} value={searchInDoc} onChange={(e) => setSearchInDoc(e.target.value)} />
            <div className="doc-tree p-2">{renderTree(filteredTree)}</div>
          </Offcanvas.Body>
        </Offcanvas>
      </Container>

      <RedactionCompareModal
        show={showCompareModal}
        onHide={() => setShowCompareModal(false)}
        docId={docId}
        currentRedid={doc?.actual_redid}
      />
    </>
  );
};
